import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type { GetTableFilterConfigResponse } from '@erp/rpc';

export function useUserTableFilterConfig(tableId: string) {
  return useQuery(
    orpc.core.userTableFilterConfigs.get.queryOptions({
      input: { tableId },
    }),
  );
}

// Shared key so overlapping filter upserts (across tables) are counted together
// — and apart from unrelated mutations — when deciding which one reconciles on
// settle.
const UPSERT_FILTER_CONFIG_MUTATION_KEY = [
  'core',
  'userTableFilterConfigs',
  'upsert',
] as const;

export function useUpsertUserTableFilterConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: UPSERT_FILTER_CONFIG_MUTATION_KEY,
    mutationFn: rpcClient.core.userTableFilterConfigs.upsert,
    // Apply the change to the cache immediately so the persisted filters read
    // back consistently without waiting for the round-trip (the URL already
    // reflects the user's choice). Rolled back on error.
    onMutate: async (variables) => {
      const queryKey = orpc.core.userTableFilterConfigs.get.queryKey({
        input: { tableId: variables.tableId },
      });
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<GetTableFilterConfigResponse>(queryKey);
      queryClient.setQueryData<GetTableFilterConfigResponse>(queryKey, {
        filters: variables.filters,
      });
      return { queryKey, previous };
    },
    onError: (_error, _variables, context) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
    },
    onSuccess: (data, variables) => {
      const queryKey = orpc.core.userTableFilterConfigs.get.queryKey({
        input: { tableId: variables.tableId },
      });
      queryClient.setQueryData<GetTableFilterConfigResponse>(queryKey, {
        filters: data.filters,
      });
    },
    onSettled: (_data, _error, _variables, context) => {
      // Once the last in-flight filter upsert settles, reconcile with the
      // server so a late or erroring mutation can't leave stale optimistic
      // state. Scope the count to this mutation key so unrelated in-flight
      // mutations don't block the reconcile, and so only the final upsert
      // invalidates (avoids refetch churn during rapid filter edits).
      const pending = queryClient.isMutating({
        mutationKey: UPSERT_FILTER_CONFIG_MUTATION_KEY,
        exact: true,
      });
      if (context && pending <= 1) {
        void queryClient.invalidateQueries({ queryKey: context.queryKey });
      }
    },
  });
}
